import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { AnnouncementBanner } from '@/components/AnnouncementBanner';
import { HeroSection } from '@/components/HeroSection';
import { SpotlightHomeWidget } from '@/components/SpotlightHomeWidget';
import { CompetitiveEventBanner } from '@/components/CompetitiveEventBanner';
import { ServerList } from '@/components/ServerList';
import { TagsFilter } from '@/components/TagsFilter';
import { GameBackdrop } from '@/components/GameBackdrop';
import { VerticalPanel } from '@/components/VerticalPanel';
import { LazyFooter, LazyGamesList, LazyMonthlyResetCountdown } from '@/components/LazyComponents';
import { SEOContentSection } from '@/components/SEOContentSection';
import { FAQSection } from '@/components/FAQSection';
import { Button } from '@/components/ui/button';
import { Trophy, Users, Eye, Plus, SlidersHorizontal } from 'lucide-react';
import { AdBanner728x90 } from '@/components/AdBanner728x90';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { UnifiedSEOManager } from '@/components/UnifiedSEOManager';
import { OptimizedResourceHints } from '@/components/OptimizedResourceHints';
import { AdvancedPerformanceMonitor } from '@/components/AdvancedPerformanceMonitor';
import { ImageOptimizationManager } from '@/components/ImageOptimizationManager';
import { CriticalCSS } from '@/components/CriticalCSS';
import { useSiteSettings } from '@/hooks/useAdminSettings';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCompetitiveEvents } from '@/hooks/useCompetitiveEvents';
import { useGame } from '@/contexts/GameContext';
import { supabase } from '@/integrations/supabase/client';

const Index = () => {
  const navigate = useNavigate();
  const { data: siteSettings } = useSiteSettings();
  const { t, language } = useLanguage();
  const { activeEvent } = useCompetitiveEvents();
  const { selectedGame } = useGame();
  const [sortBy, setSortBy] = useState<'default' | 'votes' | 'popular' | 'recent' | 'clicks' | 'competitive'>('default');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [prestigeFilter, setPrestigeFilter] = useState<'all' | 'prestige-only' | 'regular-only'>('all');
  const [isCompetitiveMode, setIsCompetitiveMode] = useState(false);
  const [allGamesBackdrop, setAllGamesBackdrop] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const clearTags = () => {
    setSelectedTags([]);
  };

  useEffect(() => {
    const loadAllGamesBackdrop = async () => {
      const { data } = await supabase.from('site_settings').select('setting_value').eq('setting_key', 'all_games_backdrop_url').single();
      if (data?.setting_value) {
        setAllGamesBackdrop(data.setting_value as string);
      }
    };
    loadAllGamesBackdrop();
  }, []);

  const navigateToAddServer = () => {
    let path = '/add-server';
    if (language === 'en') path = '/en/add-server';
    else if (language === 'es') path = '/es/add-server';
    navigate(path);
  };

  useEffect(() => {
    if (sortBy === 'competitive') {
      setIsCompetitiveMode(true);
    } else {
      setIsCompetitiveMode(false);
    }
  }, [sortBy]);

  useEffect(() => {
    if (!activeEvent && sortBy === 'competitive') {
      setSortBy('default');
      setIsCompetitiveMode(false);
    }
  }, [activeEvent, sortBy]);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [{
      "@type": "WebSite",
      "@id": `${window.location.origin}#website`,
      "name": siteSettings?.site_name || "RankServeur",
      "description": siteSettings?.site_description || "Découvrez et votez pour les meilleurs serveurs de jeux",
      "url": window.location.origin,
      "inLanguage": language,
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${window.location.origin}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      },
      "publisher": {
        "@type": "Organization",
        "name": "RankServeur Team",
        "@id": `${window.location.origin}#organization`
      }
    }, {
      "@type": "Organization",
      "@id": `${window.location.origin}#organization`,
      "name": "RankServeur Team",
      "url": window.location.origin,
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/assets/logo.png`,
        "name": "RankServeur Logo"
      },
      "sameAs": ["https://twitter.com/rankserveur_official"],
      "contactPoint": {
        "@type": "ContactPoint",
        "email": siteSettings?.contact_email || "services@rankserveur.com",
        "contactType": "customer service",
        "availableLanguage": ["French", "English", "Spanish"]
      }
    }, {
      "@type": "WebPage",
      "@id": `${window.location.href}#webpage`,
      "url": window.location.href,
      "name": `Accueil - ${siteSettings?.site_name || "RankServeur"}`,
      "description": siteSettings?.site_description || "Découvrez et votez pour les meilleurs serveurs de jeux",
      "inLanguage": language,
      "isPartOf": { "@id": `${window.location.origin}#website` },
      "about": {
        "@type": "Thing",
        "name": "Serveurs de jeux en ligne",
        "description": "Plateforme de classement et de vote pour serveurs de jeux multijoueurs"
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": window.location.origin
        }]
      }
    }]
  };

  const sortOptions = [
    { value: 'votes', label: t('tags.moreVotes') },
    { value: 'popular', label: t('tags.morePopular') },
    { value: 'recent', label: t('tags.latestServers') },
    { value: 'clicks', label: t('tags.moreClicks') },
  ];

  const activeFiltersCount = (sortBy !== 'default' ? 1 : 0) + (prestigeFilter !== 'all' ? 1 : 0) + selectedTags.length;

  return (
    <div className="min-h-screen bg-gradient-main relative">
      <CriticalCSS />
      <OptimizedResourceHints />
      <AdvancedPerformanceMonitor />
      <ImageOptimizationManager />
      <UnifiedSEOManager type="homepage" structuredData={structuredData} />

      <GameBackdrop
        backdropUrl={selectedGame?.backdrop_url || allGamesBackdrop || null}
        gameName={selectedGame?.name || 'Tous les jeux'}
        className="fixed inset-0 z-0"
      />

      <div className="relative z-10">
        <Header />
        <AnnouncementBanner />
        <main>
          {/* Hero compact */}
          <div className="container mx-auto px-4 pt-4 sm:pt-8 pb-4 text-neutral-100">
            <div className="text-center max-w-4xl mx-auto space-y-3">
              <p
                style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
                className="text-sm sm:text-base md:text-lg max-w-3xl mx-auto leading-relaxed font-medium text-neutral-400"
              >
                {language === 'fr'
                  ? 'Une plateforme communautaire pour explorer et voter chaque jour pour les serveurs privés qui vous passionnent.'
                  : language === 'es'
                  ? 'Una plataforma comunitaria para explorar y votar diariamente por los servidores privados que te apasionan.'
                  : "A community platform to explore and vote daily for the private servers you're passionate about."}
              </p>
            </div>
          </div>

          <HeroSection />

          <div className="container mx-auto px-4">
            <CompetitiveEventBanner />
          </div>

          <SpotlightHomeWidget />

          <div className="container mx-auto px-4 py-6">
            {/* Barre compacte : boutons à gauche, reset à droite */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Button
                size="sm"
                onClick={navigateToAddServer}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold text-xs border-0 transition-all duration-200 h-8 px-3"
                aria-label={t('index.addMyServer')}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                {t('index.addMyServer')}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`border-slate-600 text-slate-300 hover:text-white h-8 px-3 text-xs ${showFilters ? 'bg-slate-700 text-white' : ''}`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />
                {language === 'fr' ? 'Filtres' : language === 'es' ? 'Filtros' : 'Filters'}
                {activeFiltersCount > 0 && (
                  <span className="ml-1 bg-blue-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>

              <div className="ml-auto">
                <LazyMonthlyResetCountdown variant="mini" />
              </div>
            </div>

            {/* Panneau filtres dépliable */}
            {showFilters && (
              <div className="mb-5 p-4 bg-slate-800/60 rounded-lg border border-slate-700/50 space-y-4 animate-in slide-in-from-top-2 duration-200">
                {/* Tags */}
                <TagsFilter selectedTags={selectedTags} onTagToggle={handleTagToggle} onClearTags={clearTags} />

                {/* Tri */}
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-slate-400 font-medium mr-1">
                    {language === 'fr' ? 'Trier :' : language === 'es' ? 'Ordenar:' : 'Sort:'}
                  </span>
                  {sortOptions.map((option) => (
                    <Button
                      key={option.value}
                      variant={sortBy === option.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() =>
                        setSortBy(
                          option.value as 'default' | 'votes' | 'popular' | 'recent' | 'clicks' | 'competitive'
                        )
                      }
                      className={`text-xs h-8 px-3 ${
                        sortBy === option.value
                          ? 'bg-gaming-primary text-white'
                          : 'border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>

                {/* Filtre Prestige */}
                <div className="flex flex-wrap gap-3 items-center">
                  <span className="text-xs text-slate-400 font-medium mr-1">
                    {t('filters.prestigeFilter')}:
                  </span>
                  {[
                    { id: 'all', label: t('filters.allServers'), value: 'all' as const },
                    { id: 'prestige-only', label: t('filters.prestigeOnly'), value: 'prestige-only' as const },
                    { id: 'regular-only', label: t('filters.regularOnly'), value: 'regular-only' as const },
                  ].map((opt) => (
                    <div key={opt.id} className="flex items-center space-x-1.5">
                      <Checkbox
                        id={opt.id}
                        checked={prestigeFilter === opt.value}
                        onCheckedChange={() => setPrestigeFilter(opt.value)}
                        className="w-4 h-4"
                      />
                      <Label htmlFor={opt.id} className="text-xs text-slate-300 cursor-pointer">
                        {opt.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bannière publicitaire */}
            <AdBanner728x90 />

            {/* Grille principale */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-1">
                <LazyGamesList />
              </div>
              <div className="lg:col-span-3">
                <ServerList
                  sortBy={sortBy}
                  selectedTags={selectedTags}
                  prestigeFilter={prestigeFilter}
                  isCompetitiveMode={isCompetitiveMode}
                />
              </div>
              <div className="hidden lg:block lg:col-span-1">
                <VerticalPanel />
              </div>
            </div>
          </div>

          {/* Section SEO bottom améliorée */}
          <div className="container mx-auto px-4 py-10 my-6">
            <FAQSection />

            <div className="max-w-4xl mx-auto space-y-8 mt-10">
              <div className="text-center space-y-4">
                <h2 className="text-2xl md:text-3xl font-bold gaming-text-gradient">
                  {language === 'fr'
                    ? 'Découvrez la Plateforme N°1 des Serveurs Privés de Jeux'
                    : language === 'es'
                    ? 'Descubre la Plataforma N°1 de Servidores Privados de Juegos'
                    : 'Discover the #1 Platform for Private Game Servers'}
                </h2>
                <p className="text-slate-300 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
                  {language === 'fr'
                    ? "RankServeur est la plateforme de référence pour découvrir, comparer et voter pour les meilleurs serveurs de jeux privés. Notre communauté active participe chaque jour au classement des serveurs les plus populaires, avec des votes en temps réel et un système de classement transparent."
                    : language === 'es'
                    ? "RankServeur es la plataforma de referencia para descubrir, comparar y votar por los mejores servidores de juegos privados. Nuestra comunidad activa participa cada día en la clasificación de los servidores más populares, con votos en tiempo real y un sistema de clasificación transparente."
                    : "RankServeur is the reference platform to discover, compare and vote for the best private game servers. Our active community participates daily in ranking the most popular servers, with real-time voting and a transparent ranking system."}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-5">
                {[
                  {
                    icon: Trophy,
                    color: 'text-gaming-primary',
                    borderColor: 'border-blue-500/20',
                    title:
                      language === 'fr'
                        ? 'Classement en Temps Réel'
                        : language === 'es'
                        ? 'Rankings en Tiempo Real'
                        : 'Real-Time Rankings',
                    desc:
                      language === 'fr'
                        ? 'Découvrez les meilleurs serveurs privés classés par votes de la communauté. Notre système de classement est actualisé en temps réel avec des votes toutes les 2 heures pour vous garantir les serveurs les plus actifs, populaires et fun du moment.'
                        : language === 'es'
                        ? 'Descubre los mejores servidores privados clasificados por votos de la comunidad. Nuestro sistema de ranking se actualiza en tiempo real con votos cada 2 horas para garantizarte los servidores más activos, populares y divertidos del momento.'
                        : 'Discover the best private servers ranked by community votes. Our ranking system is updated in real-time with votes every 2 hours to guarantee you the most active, popular and fun servers.',
                  },
                  {
                    icon: Users,
                    color: 'text-gaming-accent',
                    borderColor: 'border-green-500/20',
                    title:
                      language === 'fr'
                        ? 'Communauté Gaming Active'
                        : language === 'es'
                        ? 'Comunidad Gaming Activa'
                        : 'Active Gaming Community',
                    desc:
                      language === 'fr'
                        ? "Rejoignez une communauté gaming passionnée et trouvez les meilleurs serveurs privés pour Minecraft, Dofus, WoW, CS2, GTA RP, Rust et plus encore. Votez quotidiennement pour vos serveurs favoris."
                        : language === 'es'
                        ? "Únete a una comunidad gaming apasionada y encuentra los mejores servidores privados para Minecraft, Dofus, WoW, CS2, GTA RP, Rust y más. Vota diariamente por tus servidores favoritos."
                        : "Join a passionate gaming community and find the best private servers for Minecraft, Dofus, WoW, CS2, GTA RP, Rust and more. Vote daily for your favorite servers.",
                  },
                  {
                    icon: Eye,
                    color: 'text-gaming-warning',
                    borderColor: 'border-yellow-500/20',
                    title:
                      language === 'fr'
                        ? 'Serveurs Vérifiés et Sûrs'
                        : language === 'es'
                        ? 'Servidores Verificados y Seguros'
                        : 'Verified and Safe Servers',
                    desc:
                      language === 'fr'
                        ? "Tous les serveurs listés sont vérifiés et approuvés par notre équipe de modération pour garantir une expérience de jeu sûre et de qualité."
                        : language === 'es'
                        ? 'Todos los servidores listados son verificados y aprobados por nuestro equipo de moderación para garantizar una experiencia de juego segura y de calidad.'
                        : 'All listed servers are verified and approved by our moderation team to guarantee a safe and quality gaming experience.',
                  },
                ].map((card, i) => (
                  <div
                    key={i}
                    className={`bg-slate-800/80 backdrop-blur-sm p-5 rounded-xl border ${card.borderColor} hover:border-slate-500/40 transition-colors`}
                  >
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="p-2 rounded-lg bg-slate-700/50">
                        <card.icon className={`w-5 h-5 ${card.color}`} />
                      </div>
                      <h3 className="text-base font-bold text-white">{card.title}</h3>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{card.desc}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-4 pt-6 border-t border-slate-700/50">
                <h3 className="text-lg md:text-xl font-bold gaming-text-gradient text-center">
                  {language === 'fr'
                    ? 'Serveurs pour Tous les Styles de Jeu'
                    : language === 'es'
                    ? 'Servidores para Todos los Estilos de Juego'
                    : 'Servers for All Play Styles'}
                </h3>
                <p className="text-slate-300 text-center text-sm max-w-2xl mx-auto">
                  {language === 'fr'
                    ? 'Que vous recherchiez du PvP compétitif, du PvE coopératif, du Roleplay immersif ou simplement une communauté friendly pour vous amuser, vous trouverez votre bonheur parmi nos milliers de serveurs référencés.'
                    : language === 'es'
                    ? 'Ya sea que busques PvP competitivo, PvE cooperativo, Roleplay inmersivo o simplemente una comunidad amigable para divertirte, encontrarás tu felicidad entre nuestros miles de servidores listados.'
                    : "Whether you're looking for competitive PvP, cooperative PvE, immersive Roleplay or just a friendly community to have fun, you'll find your happiness among our thousands of listed servers."}
                </p>
              </div>
            </div>
          </div>
        </main>

        <SEOContentSection />

        <div className="h-12 bg-gradient-to-b from-transparent via-slate-900/30 to-slate-950" />

        <LazyFooter />
      </div>
    </div>
  );
};

export default Index;
